import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useRef,
} from "react";
import styles from "./Actions.module.scss";
import "./actions.css";
import Button from "../../../../components/button/Button";
import { useStudentNextContent } from "../../useStudentNextContent";
import { ContentType } from "@api/graphql-codegen";

import { create, CreateTypes } from "canvas-confetti";
import { useStudentLessonContent } from "@src/hooks/useStudentLessonContent";
import AdditionalModal from "../../additional-modal/AdditionalModal";
import CompletingModal from "../../complete-modal/CompletingModal";

type Props = {
  children: React.ReactNode;
  fullScreen: boolean;
  leftButton?: React.ReactNode;
  rightButton?: React.ReactNode;
  totalPages?: number;
  pageNumber?: number;
  className?: string;
  hideNextButton?: boolean;
  isPreview?: boolean;
  exitTicket?: boolean;
  goBack?: () => void;
  formData?: string | null;
  videoEnd?: boolean;
  handlePauseVideo?: () => void;
};

function Actions(props: Props) {
  const {
    children,
    rightButton,
    leftButton,
    totalPages,
    pageNumber,
    className = "",
    hideNextButton,
    isPreview = false,
    goBack,
    videoEnd,
    handlePauseVideo,
    fullScreen,
  } = props;
  const {
    hasNextContent,
    selectNext,
    selectPrev,
    selectExitTicket,
    hasAdditionalContent,
    hasPrevContent,
  } = useStudentNextContent();
  const {
    content,
    lesson,
    sllLesson,
    sllModule,
    lastContent,
    showExitTicket,
  } = useStudentLessonContent();

  const canvasRef = useRef<HTMLCanvasElement>(null),
    fireRef = useRef<CreateTypes | null>(null);

  const [completeModal, setCompleteModal] = useState(false);
  const [showHereModal, setShowHereModal] = useState(false);

  const [hideCanvas, setHideCanvas] = useState(true);
  const [lastPage, setLastPage] = useState(0);

  const isPhone = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [
      /(Android)(.+)(Mobile)/i,
      /BlackBerry/i,
      /iPhone|iPod/i,
      /Opera Mini/i,
      /IEMobile/i,
    ];
    return regexs.some((b) => userAgent.match(b));
  }, []);

  const lastmodule = useMemo(() => {
    const modules = [...((sllLesson?.materials || lesson?.materials) || [])];
    if (modules.length > 2) {
      const index = modules.findIndex((m) => (m?.id === sllModule?.id || m?.id === content?.id));
      if (
        index === modules.length - 2 &&
        modules.at(modules.length - 1)?.lastInLesson
      ) {
        return true;
      }
    }
    return false;
  }, [sllModule, sllLesson, content, lesson]);
  const width = useMemo(
    () =>
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth,
    []
  );
  const height = useMemo(() => window.innerHeight, []);

  useEffect(() => {
    if (canvasRef.current) {
      fireRef.current = create(canvasRef.current, {} as any);
    }
  }, []);
  const fire = useCallback(() => {
    if (fireRef.current) {
      fireRef.current({
        particleCount: 1200,
        shapes: ["circle", "square", "circle"],
        startVelocity: 50,
        spread: 360,
        origin: {
          x: Math.random(),
          y: Math.random() - 0.1,
        },
      });
    }
  }, []);

  const handleCongrates = useCallback(() => {
    setHideCanvas(false);
    if (!isPreview) {
      fire();
      setTimeout(() => {
        fire();
        setTimeout(() => {
          fire();
          setTimeout(() => {
            fire();
          }, 500);
        }, 500);
      }, 500);
    }
    setTimeout(() => {
      setHideCanvas(true);
    }, 10000);
  }, [isPreview, fire]);

  const isLastPage = useMemo(() => {
    if (pageNumber) {
      setLastPage(pageNumber);
      return (
        (totalPages === pageNumber && lastPage === totalPages - 1) ||
        (totalPages === pageNumber && totalPages === 1)
      );
    }
    return false;
  }, [totalPages, pageNumber, lastPage]);

  useEffect(() => {
    if (pageNumber) {
      if (
        lastContent &&
        (sllModule?.lastInLesson || content?.lastInLesson) &&
        !hasNextContent &&
        !isPreview &&
        isLastPage
      ) {
        setTimeout(() => {
          handleCongrates();
        }, 500);
      }
    } else {
      if (
        lastContent &&
        (sllModule?.lastInLesson || content?.lastInLesson) &&
        !hasNextContent &&
        !isPreview
      ) {
        setTimeout(() => {
          handleCongrates();
        }, 500);
      }

      if ((sllModule?.additionalAfter || content?.additionalAfter) && hasNextContent && !isPreview) {
        if ((sllModule?.type === ContentType.VIDEO || content?.type === ContentType.VIDEO) && videoEnd) {
          setShowHereModal(true);
        }
      }
    }
  }, [
    content,
    sllModule,
    handleCongrates,
    pageNumber,
    hasNextContent,
    isLastPage,
    isPreview,
    lastContent,
    videoEnd,
  ]);

  const handleGoBack = useCallback(() => {
    setShowHereModal(false);
  }, []);

  const handleAdditional = useCallback(() => {
    setShowHereModal(false);
    selectNext();
  }, [selectNext]);

  const gotoExitTicket = useCallback(() => {
    setShowHereModal(false);
    selectExitTicket();
  }, [selectExitTicket]);

  const handleComplete = useCallback(() => {
    if (!((sllModule?.lastInLesson || content?.lastInLesson) || !hasNextContent) && goBack) {
      goBack();
    } else {
      setCompleteModal(true);
    }
  }, [sllModule, content, goBack, hasNextContent]);

  const handlePrev = useCallback(() => {
    selectPrev();
  }, [selectPrev]);

  const onNext = useCallback(() => {
    if (
      (sllModule?.additionalAfter || content?.additionalAfter) &&
      hasNextContent &&
      !isPreview &&
      hasAdditionalContent
    ) {
      if (handlePauseVideo) {
        handlePauseVideo();
      }
      setShowHereModal(true);
      return;
    }

    selectNext();
  }, [
    sllModule?.additionalAfter,
    content?.additionalAfter,
    hasNextContent,
    isPreview,
    hasAdditionalContent,
    selectNext,
    handlePauseVideo,
  ]);

  const survey = useMemo(() => {
    // Exit Ticket
    if ((sllModule?.type === ContentType.FORM || content?.type === ContentType.FORM) && showExitTicket) {
      return true;
    } else {
      return false;
    }
  }, [sllModule?.type, content?.type, showExitTicket]);

  return (
    <>
      <div
        className={`${styles.content} ${className}`}
        style={{ backgroundColor: isPhone ? "#535353" : "", paddingBottom: 0 }}
      >
        {children}
        <div
          className={styles.confettiView}
          style={{ display: hideCanvas ? "none" : "block" }}
        >
          <canvas ref={canvasRef} width={width} height={height} />
        </div>
      </div>
      <div
        className={`${styles.actions} ${
          isPreview || survey
            ? !fullScreen
              ? styles.whiteBar
              : styles.blackBar
            : styles.blackBar
        }`}
      >
        {leftButton ? (
          <div className={styles.prevAction}>{leftButton}</div>
        ) : (
          <>
            {hasPrevContent ? (
              <div className={styles.prevAction}>
                <Button
                  size={"extraSmall"}
                  onClick={handlePrev}
                  outline={true}
                  label={"PREVIOUS"}
                  className={`${
                    isPreview || survey || isPhone
                      ? styles.fullScreenWhitePrevious
                      : styles.fullScreenPrevious
                  }`}
                />
              </div>
            ) : (
              <div />
            )}
          </>
        )}
        {Boolean(totalPages) && Boolean(pageNumber) && (
          <div className={styles.pages}>
            <span className={styles.currentPage}>{pageNumber}</span>/
            {totalPages}
          </div>
        )}
        {rightButton ? (
          <div className={styles.endAction}>{rightButton}</div>
        ) : (
          <>
            {hasNextContent && !hideNextButton ? (
              <div className={styles.nextActions}>
                <Button
                  size={"extraSmall"}
                  onClick={onNext}
                  color={"white"}
                  label={
                    lastmodule
                      ? "END LESSON"
                      : "CONTINUE"
                  }
                  className={styles.action}
                />
              </div>
            ) : !hasNextContent ? (
              <div className={styles.endAction}>
                <Button
                  size={"extraSmall"}
                  onClick={handleComplete}
                  color={"white"}
                  label={"END LESSON"}
                />
              </div>
            ) : null}
          </>
        )}
      </div>
      {completeModal && (
        <CompletingModal
          open={completeModal}
          onClose={() => setCompleteModal(false)}
        />
      )}
      {showHereModal && (
        <AdditionalModal
          open={showHereModal}
          onClose={handleGoBack}
          onAdditionalExperience={handleAdditional}
          onExitTicket={gotoExitTicket}
        />
      )}
    </>
  );
}

export default Actions;
